/**
 * Tools Index Page
 * 
 * Displays all 17+ prioritization tools from "Managing Priorities" book
 * organized by category. Uses internal routing within the prioritization app.
 * Requires prioritization or facilitation permissions.
 * 
 * Now integrates with shaped items via useToolItems hook.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UI_LABELS } from '../../lib/terminology';
import { PageHeader } from '../../components/prioritization/PageHeader';
import InlineToolSelector from '../../components/prioritization/InlineToolSelector';
import SessionLauncher from '../../components/prioritization/SessionLauncher';
import { 
  Grid3X3, 
  ArrowUpDown, 
  Gamepad2, 
  ArrowRight,
  Calculator,
  Scale,
  Timer,
  Boxes,
  ListOrdered,
  Vote,
  DollarSign,
  LayoutGrid,
  TreeDeciduous,
  Sailboat,
  BarChart3,
  Target,
  Zap,
  Wand2,
  Lock,
  Play,
  Rocket,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useCreateRights } from '../../lib/authorization/createRights';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { sessionDetailUrl } from '../../lib/sessions/sessionDetailUrl';
import { isAdminRole } from '../../types/prioritization/roles';
import { usePinnedCycleStore } from '../../stores/prioritization/pinnedCycleStore';
import { useContextsStore } from '../../stores/prioritization/contextsStore';
import { useConfigurationsStore } from '../../stores/prioritization/configurationsStore';
import type { ObjectType, OutputShape } from '../../lib/toolRecommendationMatrix';
import { useWorkspaceModules, isToolEnabled } from '../../lib/workspaceModulesService';

interface Tool {
  path: string;
  /** Matches the toolId in methodRegistry and TOOL_REGISTRY */
  toolId: string;
  label: string;
  description: string;
  icon: LucideIcon;
  comingSoon?: boolean;
}

interface ToolCategory {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  hoverBg: string;
  tools: Tool[];
}

export default function Tools() {
  const navigate = useNavigate();
  // Use primitive selector to avoid Zustand infinite loops
  const currentRole = useUserStore((state) => state.currentRole);
  const canAny = useUserStore((state) => state.canAny);
  const workspaceFeatures = useWorkspaceModules();

  // Canonical permission resolution (canAny): honors DB-assigned roles and
  // admin elevation. The old static roleHasPermission(currentRole, …) lookup
  // locked out workspace admins whose currentRole wasn't in the legacy map
  // (owner report 2026-07-09).
  const canUseTools =
    isAdminRole(currentRole) ||
    canAny(['process.prioritize', 'session.facilitate', 'lists.edit']);
  // Start Session creates a session, whose insert rule (migration 754) takes
  // SESSION_RUN — narrower than the page's own gate above. Drawn only for
  // holders (build contract §2.8); Quick Use stays for everyone on the page.
  const mayStartSession = useCreateRights().session;
  
  // Pull active configuration metadata so InlineToolSelector can show matrix recommendations
  const pinnedCycleId = usePinnedCycleStore((s) => s.pinnedCycleId);
  const fetchContextForCycle = useContextsStore((s) => s.fetchContextForCycle);
  const getConfigurationById = useConfigurationsStore((s) => s.getConfigurationById);
  const [activeObjectTypes, setActiveObjectTypes] = useState<ObjectType[]>([]);
  const [activeOutputShapes, setActiveOutputShapes] = useState<OutputShape[]>([]);
  const [launcherTool, setLauncherTool] = useState<{ toolId: string; toolName: string } | null>(null);

  useEffect(() => {
    if (!pinnedCycleId) return;
    void fetchContextForCycle(pinnedCycleId).then((ctx) => {
      if (!ctx?.configuration_id) return;
      const config = getConfigurationById(ctx.configuration_id);
      if (!config) return;
      setActiveObjectTypes((config.object_types ?? []) as ObjectType[]);
      setActiveOutputShapes((config.output_shapes ?? []) as OutputShape[]);
    });
  }, [pinnedCycleId, fetchContextForCycle, getConfigurationById]);
  
  if (!canUseTools) {
    return (
      <div className="space-y-6">
        <div className="card bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 border-[var(--mustard)] dark:border-[var(--mustard)]">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 rounded-full">
              <Lock className="h-6 w-6 text-[var(--mustard)] dark:text-[var(--mustard)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--mustard)] dark:text-[var(--mustard)] mb-2">Access Restricted</h2>
              <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] mb-2">
                You don't have permission to use prioritization tools. This requires one of these permissions:
              </p>
              <ul className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] list-disc list-inside ml-2">
                <li><strong>process.prioritize</strong> - Participate in prioritization</li>
                <li><strong>session.facilitate</strong> - Facilitate sessions</li>
                <li><strong>lists.edit</strong> - Edit {UI_LABELS.itemPools}</li>
              </ul>
              <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mt-2">
                Roles with these permissions: Facilitator, List Owner, Cross-Map Lead, Platform Admin
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const categories: ToolCategory[] = [
    {
      id: 'frameworks',
      name: 'Decision Frameworks',
      description: 'Structured approaches with specific criteria, matrices, and categorization systems',
      icon: Grid3X3,
      color: 'text-[var(--text-sec)]',
      bgColor: 'bg-[var(--slate-dim)]',
      borderColor: 'border-[var(--slate)]',
      hoverBg: 'hover:bg-[var(--slate-dim)]',
      tools: [
        { path: 'eisenhower-matrix', toolId: 'eisenhower-matrix', label: 'Eisenhower Matrix', description: 'Urgent vs Important 2×2 matrix', icon: LayoutGrid },
        { path: 'moscow-method', toolId: 'moscow-method', label: 'MoSCoW Method', description: 'Must, Should, Could, Won\'t categorization', icon: Boxes },
        { path: 'impact-effort-matrix', toolId: 'impact-effort-matrix', label: 'Impact / Effort Matrix', description: 'Plot items by value and cost', icon: Scale },
        { path: 'max-priorities-pyramid', toolId: 'max-priorities-pyramid', label: 'Max Priorities Pyramid', description: 'Constrained focus hierarchy', icon: Target },
        { path: 'fast-vs-right', toolId: 'fast-vs-right', label: 'Fast vs Right Framework', description: 'Speed vs Quality tradeoffs', icon: Zap },
        { path: 'custom-matrix', toolId: 'custom-matrix', label: 'Custom 2×2 Matrix', description: 'Build and save your own named 2×2 matrix', icon: LayoutGrid },
      ],
    },
    {
      id: 'scoring',
      name: 'Scoring Methods',
      description: 'Quantitative approaches that calculate priority scores based on multiple factors',
      icon: Calculator,
      color: 'text-[var(--green)]',
      bgColor: 'bg-[var(--green-dim)]',
      borderColor: 'border-[var(--green)]',
      hoverBg: 'hover:bg-[var(--green-dim)]',
      tools: [
        { path: 'rice-scoring', toolId: 'rice-scoring', label: 'RICE Scoring', description: 'Reach × Impact × Confidence ÷ Effort', icon: Calculator },
        { path: 'wsjf', toolId: 'wsjf', label: 'WSJF', description: 'Weighted Shortest Job First (SAFe)', icon: Timer },
        { path: 'simple-weighted-scoring', toolId: 'simple-weighted-scoring', label: 'Weighted Scoring', description: 'Custom criteria with weights', icon: Scale },
        { path: 'cost-of-delay', toolId: 'cost-of-delay', label: 'Cost of Delay', description: 'Time-based value calculations', icon: Timer },
        { path: 'magic-prioritization', toolId: 'magic-prioritization', label: 'Magic Prioritization', description: 'Staged decoupling to surface CD3', icon: Wand2 },
        { path: 'pareto-analysis', toolId: 'pareto-analysis', label: 'Pareto Analysis', description: '80/20 rule application', icon: BarChart3 },
      ],
    },
    {
      id: 'ranking',
      name: 'Ranking Techniques',
      description: 'Methods that order items through direct comparison or forced ranking',
      icon: ArrowUpDown,
      color: 'text-[var(--mustard)]',
      bgColor: 'bg-[var(--mustard-dim)]',
      borderColor: 'border-[var(--mustard)]',
      hoverBg: 'hover:bg-[var(--mustard-dim)]',
      tools: [
        { path: 'stack-ranking', toolId: 'stack-ranking', label: 'Stack Ranking', description: 'Forced linear ordering', icon: ListOrdered },
        { path: 'pairwise-ranking', toolId: 'pairwise-ranking', label: 'Pairwise Comparison', description: 'Compare items two at a time', icon: ArrowUpDown },
      ],
    },
    {
      id: 'collaborative',
      name: 'Collaborative Games',
      description: 'Interactive methods for group prioritization and stakeholder engagement',
      icon: Gamepad2,
      color: 'text-[var(--text-sec)]',
      bgColor: 'bg-[var(--slate-dim)]',
      borderColor: 'border-[var(--slate)]',
      hoverBg: 'hover:bg-[var(--slate-dim)]',
      tools: [
        { path: 'dot-voting', toolId: 'dot-voting', label: 'Dot Voting', description: 'Distribute votes across options', icon: Vote },
        { path: 'buy-a-feature', toolId: 'buy-a-feature', label: 'Buy A Feature', description: 'Allocate budget to priorities', icon: DollarSign },
        { path: 'speed-boat', toolId: 'speed-boat', label: 'Speed Boat', description: 'Identify anchors and engines', icon: Sailboat },
        { path: 'prune-product-tree', toolId: 'prune-product-tree', label: 'Prune the Tree', description: 'Trim features from product tree', icon: TreeDeciduous },
      ],
    },
  ];

  // Filter tools within each category based on workspace module state
  const filteredCategories = categories.map((cat) => ({
    ...cat,
    tools: cat.tools.filter((tool) => isToolEnabled(workspaceFeatures, tool.toolId)),
  })).filter((cat) => cat.tools.length > 0);

  const totalTools = filteredCategories.reduce((sum, cat) => sum + cat.tools.length, 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <PageHeader
        icon={<Wrench className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Prioritization Tools"
        subtitle={`${totalTools} interactive tools to help you make better decisions — including custom 2×2 matrices.`}
      />

      {/* Inline Tool Selector — passes active configuration metadata for matrix recommendations */}
      <InlineToolSelector
        objectTypes={activeObjectTypes.length ? activeObjectTypes : undefined}
        outputShapes={activeOutputShapes.length ? activeOutputShapes : undefined}
      />

      {/* Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCategories.map((category) => {
          const CategoryIcon = category.icon;
          return (
            <div key={category.id} className="card">
              {/* Category Header */}
              <div className={`flex items-start gap-4 p-4 dark:bg-[var(--bg-raised)]/50 ${category.bgColor}  border ${category.borderColor} mb-4`}>
                <div className={`p-2 bg-[var(--bg-raised)]  border ${category.borderColor} shadow-sm`}>
                  <CategoryIcon className={`h-6 w-6 ${category.color}`} />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">{category.name}</h2>
                  <p className="text-sm text-[var(--text-sec)] mt-0.5">{category.description}</p>
                </div>
                <span className={`text-sm font-medium ${category.color}`}>
                  {category.tools.length} tools
                </span>
              </div>

              {/* Tools List */}
              <div className="space-y-2">
                {category.tools.map((tool) => {
                  const ToolIcon = tool.icon;
                  if (tool.comingSoon) {
                    return (
                      <div
                        key={tool.path}
                        className="flex items-center gap-3 p-3 border border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 opacity-75"
                      >
                        <ToolIcon className="h-5 w-5 text-[var(--text-ter)]" />
                        <div className="flex-1">
                          <span className="font-medium text-[var(--text-sec)]">{tool.label}</span>
                          <p className="text-xs text-[var(--text-ter)]">{tool.description}</p>
                        </div>
                        <span className="text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-1 rounded-full">
                          Coming Soon
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={tool.path}
                      className={`group flex items-center gap-3 p-3  border border-[var(--rule)] dark:hover:bg-[var(--bg-raised)] ${category.hoverBg} transition-all duration-200 hover:shadow-sm`}
                    >
                      <ToolIcon className={`h-5 w-5 text-[var(--text-ter)] group-hover:${category.color} transition-colors flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <Link to={tool.path} className="block">
                          <span className="font-medium text-[var(--text-pri)] group-hover:text-[var(--text-pri)] dark:group-hover:text-white">{tool.label}</span>
                          <p className="text-xs text-[var(--text-ter)]">{tool.description}</p>
                        </Link>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Start Session — opens the launcher drawer */}
                        {mayStartSession && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setLauncherTool({ toolId: tool.toolId, toolName: tool.label });
                            }}
                            className="hidden group-hover:flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/40 rounded hover:bg-primary-100 dark:hover:bg-primary-900/60 transition-colors"
                            title="Start a session with this tool"
                          >
                            <Rocket className="h-3 w-3" />
                            Start Session
                          </button>
                        )}
                        {/* Quick Use: open standalone */}
                        <Link
                          to={`${tool.path}?mode=standalone`}
                          onClick={(e) => e.stopPropagation()}
                          className="hidden group-hover:flex items-center gap-1 px-2 py-1 text-xs font-medium text-[var(--text-ter)] bg-[var(--hover-tint)] rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--surface)] transition-colors"
                          title="Quick use without a session"
                        >
                          <Play className="h-3 w-3" />
                          Quick Use
                        </Link>
                        <Link to={tool.path}>
                          <ArrowRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)] transition-colors" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Audit trail: tool selector history */}
      {/* <ToolSelectorHistory /> */}

      {/* Tool-first session launcher: the new session opens where a link to
          it would (its room when live-timed, otherwise its detail page). */}
      {launcherTool && (
        <SessionLauncher
          toolId={launcherTool.toolId}
          toolName={launcherTool.toolName}
          isOpen={true}
          onClose={() => setLauncherTool(null)}
          onCreated={(sessionId) => {
            setLauncherTool(null);
            const created = useSessionStore.getState().getSession(sessionId);
            navigate(created ? sessionDetailUrl(created) : `/prioritization/sessions/${sessionId}`);
          }}
        />
      )}
    </div>
  );
}
